import React from 'react';
import { TrendingUp, AlertTriangle, Target } from 'lucide-react';
import { useDeals } from '../../store/DealsContext';
import PredictionChart from './PredictionChart';
import { calculateSuccessProbability, predictDealTiming } from '../../utils/predictions';

export default function PredictiveAnalytics() {
  const { deals } = useDeals();
  
  const predictions = {
    successProbability: calculateSuccessProbability(deals),
    expectedClosing: predictDealTiming(deals),
    riskFactors: [
      { factor: 'Market Volatility', impact: 'high' },
      { factor: 'Regulatory Changes', impact: 'medium' },
      { factor: 'Integration Complexity', impact: 'medium' }
    ]
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-indigo-900">Success Probability</h3>
            <Target className="w-6 h-6 text-indigo-600" />
          </div>
          <p className="text-2xl font-bold text-indigo-900">{predictions.successProbability}%</p>
          <p className="mt-2 text-sm text-indigo-700">Based on historical data</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-emerald-900">Expected Timeline</h3>
            <TrendingUp className="w-6 h-6 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-emerald-900">{predictions.expectedClosing} days</p>
          <p className="mt-2 text-sm text-emerald-700">Average closing time</p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-amber-900">Risk Level</h3>
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-amber-900">Medium</p>
          <p className="mt-2 text-sm text-amber-700">Based on 3 key factors</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Success Prediction Trend</h3>
          <PredictionChart />
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Risk Factors</h3>
          <div className="space-y-4">
            {predictions.riskFactors.map((risk) => (
              <div key={risk.factor} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{risk.factor}</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  risk.impact === 'high' 
                    ? 'bg-red-100 text-red-800'
                    : risk.impact === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {risk.impact.charAt(0).toUpperCase() + risk.impact.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}